/**
 * Kiosk Mode UI Handler
 * Handles special UI features and interactions for kiosk mode
 */

import { KIOSK_CONFIG } from '../config/kioskConfig.js';

class KioskModeHandler {
  constructor() {
    this.isKiosk = false;
    this.inactivityTimer = null;
    this.inactivityTimeout = KIOSK_CONFIG.INACTIVITY_TIMEOUT;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.isLongPress = false;
    this.longPressTimer = null;
    this.longPressDelay = KIOSK_CONFIG.LONG_PRESS_DELAY;
    this.helpOverlay = null;
    this.attractModeTimer = null;
    this.attractModeTimeout = KIOSK_CONFIG.ATTRACT_MODE_TIMEOUT;
    this.currentKioskStep = 'cover'; // Start with cover/attract mode
    this._isEnteringAttractMode = false; // Prevent multiple calls
    this.init();
  }

  /**
   * Initialize kiosk mode handler
   */
  init() {
    this.checkKioskMode();
    if (this.isKiosk) {
      this.setupKioskUI();
      this.bindEvents();
      this.startInactivityTimer();
      // Show attract overlay immediately on first load
      this.enterAttractMode();
      console.log('[KioskHandler] Kiosk mode initialized');
    }
  }

  /**
   * Check if kiosk mode is enabled
   */
  checkKioskMode() {
    try {
      const params = new URLSearchParams(window.location.search);
      const kioskParam = params.get('kiosk');
      this.isKiosk = kioskParam === 'true' || kioskParam === '1';
    } catch (e) {
      this.isKiosk = false;
    }
  }

  /**
   * Setup kiosk mode UI elements
   */
  setupKioskUI() {
    this.setupAttractMode();
    this.hideBrowserUI();
  }

  /**
   * Setup attract mode (screensaver-like animation)
   */
  setupAttractMode() {
    // Attract overlay is controlled by timers/flow in this handler.
    // Keep this method for backward compatibility, but don't depend on config flags.
  }

  /**
   * Create attract mode overlay
   */
  createAttractOverlay() {
    const attractOverlay = document.createElement('div');
    attractOverlay.id = 'kiosk-attract-overlay';
    attractOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: transparent;
      z-index: 15000;
      display: none;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      color: white;
      font-family: 'Roboto', sans-serif;
    `;

    // Get current language
    const lang = 'en';
    const attractContent = KIOSK_CONFIG.ATTRACT_CONTENT;

    attractOverlay.innerHTML = `
      <img src="/kiosk/Cover.png" alt="Cover" style="
        width: 100%;
        height: 100%;
        object-fit: cover;
        position: absolute;
        top: 0;
        left: 0;
      " />
      <img
        src="/kiosk/Name.png"
        alt="Name"
        class="kiosk-attract-name"
        style="
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          width: 80vw;
          height: auto;
          z-index: 2;
          pointer-events: none;
        "
      />
      <img
        src="/kiosk/Text.png"
        alt="Text"
        class="kiosk-attract-text"
        style="
          position: absolute;
          left: 50%;
          top: 75%;
          transform: translate(-50%, -50%);
          width: 56vw;
          height: auto;
          z-index: 2;
          pointer-events: none;
        "
      />
      <img
        src="/kiosk/StartPageButton.png"
        alt="Start"
        class="kiosk-start-page-button"
        style="
          position: absolute;
          left: 50%;
          bottom: calc(34px + max(env(safe-area-inset-bottom, 0px), 24px));
          transform: translateX(-50%);
          width: clamp(120px, 10vw, 10vw);
          height: auto;
          z-index: 2;
          pointer-events: none;
        "
      />
    `;

    // Add pulse animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0%, 100% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.1); opacity: 0.8; }
      }

      @keyframes kioskStartFloat {
        0%, 100% { transform: translateX(-50%) translateY(0) scale(1); }
        50% { transform: translateX(-50%) translateY(-8px) scale(1.02); }
      }

      #kiosk-attract-overlay .kiosk-start-page-button {
        animation: kioskStartFloat 1.6s ease-in-out infinite;
        filter: drop-shadow(0 10px 18px rgba(0, 0, 0, 0.28));
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(attractOverlay);

    // Touch to exit attract mode
    attractOverlay.addEventListener('touchstart', () => {
      // Add 0.2s delay transition
      setTimeout(() => {
        this.exitAttractMode();
      }, 200);
    });

    attractOverlay.addEventListener('click', () => {
      // Add 0.2s delay transition
      setTimeout(() => {
        this.exitAttractMode();
      }, 200);
    });
  }

  /**
   * Bind kiosk-specific events
   */
  bindEvents() {
    // Touch events for mobile
    document.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
    document.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    document.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: false });

    // Mouse events for desktop testing
    document.addEventListener('mousedown', this.handleMouseDown.bind(this));
    document.addEventListener('mousemove', this.handleMouseMove.bind(this));
    document.addEventListener('mouseup', this.handleMouseUp.bind(this));

    // Keyboard events
    document.addEventListener('keydown', this.handleKeyDown.bind(this));

    // Visibility change (to handle tab switching)
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

    // Prevent context menu if enabled
    if (KIOSK_CONFIG.PREVENT_CONTEXT_MENU) {
      document.addEventListener('contextmenu', (e) => {
        if (this.isKiosk) {
          e.preventDefault();
          return false;
        }
      });
    }

    // Prevent text selection if enabled
    if (KIOSK_CONFIG.PREVENT_TEXT_SELECTION) {
      document.addEventListener('selectstart', (e) => {
        if (this.isKiosk) {
          e.preventDefault();
          return false;
        }
      });
    }

    // Prevent drag and drop if enabled
    if (KIOSK_CONFIG.PREVENT_DRAG_DROP) {
      document.addEventListener('dragstart', (e) => {
        if (this.isKiosk) {
          e.preventDefault();
          return false;
        }
      });
    }
  }

  /**
   * Handle touch start
   */
  handleTouchStart(e) {
    this.resetInactivityTimer();
    this.touchStartX = e.touches[0].clientX;
    this.touchStartY = e.touches[0].clientY;
    this.isLongPress = false;

    // Start long press timer
    this.longPressTimer = setTimeout(() => {
      this.isLongPress = true;
      this.handleLongPress(e);
    }, this.longPressDelay);
  }

  /**
   * Handle touch move
   */
  handleTouchMove(e) {
    // Cancel long press if finger moved
    const touchX = e.touches[0].clientX;
    const touchY = e.touches[0].clientY;
    const moveThreshold = 10;

    if (Math.abs(touchX - this.touchStartX) > moveThreshold || 
        Math.abs(touchY - this.touchStartY) > moveThreshold) {
      this.cancelLongPress();
    }
  }

  /**
   * Handle touch end
   */
  handleTouchEnd(e) {
    this.cancelLongPress();
  }

  /**
   * Handle mouse down (for desktop testing)
   */
  handleMouseDown(e) {
    this.resetInactivityTimer();
    this.touchStartX = e.clientX;
    this.touchStartY = e.clientY;
    this.isLongPress = false;

    this.longPressTimer = setTimeout(() => {
      this.isLongPress = true;
      this.handleLongPress(e);
    }, this.longPressDelay);
  }

  /**
   * Handle mouse move
   */
  handleMouseMove(e) {
    const moveThreshold = 10;
    if (Math.abs(e.clientX - this.touchStartX) > moveThreshold || 
        Math.abs(e.clientY - this.touchStartY) > moveThreshold) {
      this.cancelLongPress();
    }
  }

  /**
   * Handle mouse up
   */
  handleMouseUp(e) {
    this.cancelLongPress();
  }

  /**
   * Handle long press
   */
  handleLongPress(e) {
    console.log('[KioskHandler] Long press detected - showing reset option');
    this.showResetConfirmation();
  }

  /**
   * Cancel long press timer
   */
  cancelLongPress() {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  /**
   * Handle keyboard events
   */
  handleKeyDown(e) {
    this.resetInactivityTimer();
    
    // Escape key functionality removed
    
    // Ctrl+R or F5 for reset (in development)
    if ((e.ctrlKey && e.key === 'r') || e.key === 'F5') {
      e.preventDefault();
      this.handleReset();
    }
  }

  /**
   * Handle visibility change
   */
  handleVisibilityChange() {
    if (document.hidden) {
      // Page is hidden, start attract mode timer
      this.startAttractModeTimer();
    } else {
      // Page is visible, cancel attract mode timer
      this.cancelAttractModeTimer();
      // Don't automatically exit attract mode - let user interaction handle it
    }
  }

  /**
   * Start inactivity timer
   */
  startInactivityTimer() {
    this.cancelInactivityTimer();
    if (!this.inactivityTimeout || this.inactivityTimeout <= 0) {
      return;
    }
    this.inactivityTimer = setTimeout(() => {
      console.log('[KioskHandler] Inactivity detected - starting attract mode');
      this.enterAttractMode();
    }, this.inactivityTimeout);
  }

  /**
   * Cancel inactivity timer
   */
  cancelInactivityTimer() {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  /**
   * Reset inactivity timer
   */
  resetInactivityTimer() {
    if (this.isKiosk) {
      this.startInactivityTimer();
    }
  }

  /**
   * Start attract mode timer
   */
  startAttractModeTimer() {
    this.cancelAttractModeTimer();
    if (!this.attractModeTimeout || this.attractModeTimeout <= 0) {
      return;
    }
    this.attractModeTimer = setTimeout(() => {
      this.enterAttractMode();
    }, this.attractModeTimeout);
  }

  /**
   * Cancel attract mode timer
   */
  cancelAttractModeTimer() {
    if (this.attractModeTimer) {
      clearTimeout(this.attractModeTimer);
      this.attractModeTimer = null;
    }
  }

  /**
   * Enter attract mode
   */
  enterAttractMode() {
    // Prevent multiple calls
    if (this._isEnteringAttractMode) {
      return;
    }
    this._isEnteringAttractMode = true;
    
    // Hide theme selection
    const closeEvent = new CustomEvent('THEME_SELECTION_CLOSE');
    window.dispatchEvent(closeEvent);
    
    // Create and show overlay immediately
    this.createAttractOverlay();
    const overlay = document.getElementById('kiosk-attract-overlay');
    if (overlay) {
      overlay.style.display = 'flex';
      this.currentKioskStep = 'cover';
    }
    
    setTimeout(() => {
      this._isEnteringAttractMode = false;
    }, 300);
  }

  /**
   * Exit attract mode
   */
  exitAttractMode() {
    const attractOverlay = document.getElementById('kiosk-attract-overlay');
    if (attractOverlay) {
      // Hide the overlay first
      attractOverlay.style.display = 'none';
      
      // Remove the overlay from DOM completely
      if (attractOverlay.parentNode) {
        attractOverlay.parentNode.removeChild(attractOverlay);
      }

      // After leaving attract/cover overlay, move into theme selection step.
      // This keeps the back-button flow consistent on first load.
      this.currentKioskStep = 'theme-selection';
      try {
        const event = new CustomEvent('SHOW_THEME_SELECTION');
        window.dispatchEvent(event);
      } catch (e) {
        // no-op
      }

      this.resetInactivityTimer();
      console.log('[KioskHandler] Exited attract mode and removed overlay');
    }
  }


  /**
   * Show reset confirmation
   */
  showResetConfirmation() {
    const lang = 'en';
    const resetContent = KIOSK_CONFIG.RESET_CONFIRMATION;
    
    const confirmation = document.createElement('div');
    confirmation.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 2rem;
      border-radius: 15px;
      text-align: center;
      z-index: 30000;
      font-family: 'Roboto', sans-serif;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    `;

    confirmation.innerHTML = `
      <h3 style="margin-bottom: 1rem; font-size: 1.5rem;">${resetContent.title[lang]}</h3>
      <p style="margin-bottom: 1.5rem; opacity: 0.8;">${resetContent.message[lang]}</p>
      <div style="display: flex; gap: 1rem; justify-content: center;">
        <button id="confirm-reset" style="
          background: #e74c3c;
          color: white;
          border: none;
          padding: 0.8rem 1.5rem;
          border-radius: 8px;
          cursor: pointer;
          font-size: 1rem;
        ">${resetContent.confirmButton[lang]}</button>
        <button id="cancel-reset" style="
          background: #667eea;
          color: white;
          border: none;
          padding: 0.8rem 1.5rem;
          border-radius: 8px;
          cursor: pointer;
          font-size: 1rem;
        ">${resetContent.cancelButton[lang]}</button>
      </div>
    `;

    document.body.appendChild(confirmation);

    // Handle confirmation
    document.getElementById('confirm-reset').addEventListener('click', () => {
      document.body.removeChild(confirmation);
      this.handleReset();
    });

    document.getElementById('cancel-reset').addEventListener('click', () => {
      document.body.removeChild(confirmation);
      this.resetInactivityTimer();
    });

    // Auto-cancel after 5 seconds
    setTimeout(() => {
      if (confirmation.parentNode) {
        document.body.removeChild(confirmation);
        this.resetInactivityTimer();
      }
    }, 5000);
  }

  /**
   * Handle reset action
   */
  handleReset() {
    console.log('[KioskHandler] Resetting application');
    
    // Clear localStorage if enabled
    if (KIOSK_CONFIG.CLEAR_LOCALSTORAGE) {
      try {
        localStorage.clear();
      } catch (e) {
        console.warn('[KioskHandler] Failed to clear localStorage:', e);
      }
    }

    // Reset the application
    window.location.reload();
  }

  /**
   * Hide browser UI elements
   */
  hideBrowserUI() {
    // Prevent scrolling
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100vw';
    document.body.style.height = '100vh';
    document.body.style.top = '0';
    document.body.style.left = '0';

    // Prevent zoom if enabled
    if (KIOSK_CONFIG.DISABLE_ZOOM) {
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
      }
    }

    // Prevent pull-to-refresh
    document.body.style.overscrollBehavior = 'none';
    document.body.style.webkitOverflowScrolling = 'touch';
  }

  /**
   * Show admin panel (hidden feature)
   */
  showAdminPanel() {
    console.log('[KioskHandler] Admin panel accessed - reset button removed in kiosk mode');
    
    const configInfo = `
      Kiosk Mode Configuration
      =======================
      • Inactivity timeout: ${this.inactivityTimeout/1000}s
      • Attract mode: ${this.attractModeTimeout/1000}s
      • Long press: ${this.longPressDelay/1000}s
      • Help auto-show: ${KIOSK_CONFIG.HELP_AUTO_SHOW}
      • Prevent context menu: ${KIOSK_CONFIG.PREVENT_CONTEXT_MENU}
      • Disable zoom: ${KIOSK_CONFIG.DISABLE_ZOOM}
      • Clear localStorage: ${KIOSK_CONFIG.CLEAR_LOCALSTORAGE}
      • Reset button: REMOVED
      • Right panel: HIDDEN
    `;
    
    if (KIOSK_CONFIG.ADMIN_DEBUG_MODE) {
      alert(configInfo);
    } else {
      console.log(configInfo);
    }
  }

  /**
   * Handle kiosk back button navigation
   */
  handleKioskBack() {
    console.log('[KioskHandler] Back button clicked, current step:', this.currentKioskStep);

    // Close top-most overlays first (designer overlays)
    try {
      const textOverlay = document.querySelector('.kiosk-text-editor-overlay');
      if (textOverlay) {
        window.dispatchEvent(new CustomEvent('KIOSK_CLOSE_TEXT_OVERLAY'));
        return;
      }
    } catch (e) {}

    try {
      const stickerOverlay = document.querySelector('.kiosk-sticker-panel-overlay');
      if (stickerOverlay) {
        window.dispatchEvent(new CustomEvent('KIOSK_CLOSE_STICKER_OVERLAY'));
        return;
      }
    } catch (e) {}

    // Help overlay functionality removed
    
    // If we're in theme selection, show attract overlay
    if (this.currentKioskStep === 'theme-selection') {
      console.log('[KioskHandler] Going back to attract mode from theme selection');
      this.enterAttractMode();
    } 
    // If we're in product selection, go back to theme selection
    else if (this.currentKioskStep === 'product-selection') {
      console.log('[KioskHandler] Going back to theme selection from product selection');
      this.currentKioskStep = 'theme-selection';
      const event = new CustomEvent('SHOW_THEME_SELECTION');
      window.dispatchEvent(event);
    }
    // If we're in designer, go back to theme selection
    else if (this.currentKioskStep === 'designer') {
      console.log('[KioskHandler] Going back to product selection from designer');
      this.currentKioskStep = 'product-selection';
      const event = new CustomEvent('SHOW_PRODUCT_SELECTION');
      window.dispatchEvent(event);
    } 
    // If we're in cover/attract mode, go to theme selection
    else if (this.currentKioskStep === 'cover') {
      console.log('[KioskHandler] Going to theme selection from cover mode');
      this.currentKioskStep = 'theme-selection';
      
      // Dispatch event to show theme selection
      const event = new CustomEvent('SHOW_THEME_SELECTION');
      window.dispatchEvent(event);
    } else {
      console.log('[KioskHandler] Unknown kiosk step:', this.currentKioskStep);
      // Default to theme selection for any unknown step
      this.currentKioskStep = 'theme-selection';
      const event = new CustomEvent('SHOW_THEME_SELECTION');
      window.dispatchEvent(event);
    }
  }

  /**
   * Set current kiosk step
   */
  setKioskStep(step) {
    this.currentKioskStep = step;
    console.log('[KioskHandler] Kiosk step set to:', step);
  }

  /**
   * Update configuration
   */
  updateConfig(config = {}) {
    if (config.inactivityTimeout) {
      this.inactivityTimeout = config.inactivityTimeout;
    }
    if (config.attractModeTimeout) {
      this.attractModeTimeout = config.attractModeTimeout;
    }
    if (config.longPressDelay) {
      this.longPressDelay = config.longPressDelay;
    }
    
    console.log('[KioskHandler] Configuration updated:', config);
  }

  /**
   * Destroy kiosk handler
   */
  destroy() {
    this.cancelInactivityTimer();
    this.cancelAttractModeTimer();
    this.cancelLongPress();
    
    // Remove UI elements
    if (this.helpOverlay && this.helpOverlay.parentNode) {
      this.helpOverlay.parentNode.removeChild(this.helpOverlay);
    }
    
    // Remove attract overlay
    const attractOverlay = document.getElementById('kiosk-attract-overlay');
    if (attractOverlay && attractOverlay.parentNode) {
      attractOverlay.parentNode.removeChild(attractOverlay);
    }
    
    console.log('[KioskHandler] Kiosk mode destroyed');
  }
}

// Initialize kiosk handler when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.kioskHandler = new KioskModeHandler();
});

// Also initialize immediately if DOM is already loaded
if (document.readyState === 'loading') {
  // DOM is still loading
} else {
  // DOM is already loaded
  if (!window.kioskHandler) {
    window.kioskHandler = new KioskModeHandler();
  }
}

// Export for use in other modules
export default KioskModeHandler;
